const PLANNING_STATE_CHANGE_CHANNEL = "grain-planning-state-change";
const PORTAIL_MUTATION_SESSION_KEY = "grain-last-portail-mutation-token";
const DATA_MANAGER_PORTAIL_SYNC_SESSION_KEY = "grain-data-manager-synced-portail-token";

let planningStateChangeToken = 0;
const planningStateChangeListeners = new Set();
let broadcastChannel = null;

const PERFORMANCE_CHAIN_ID_PATTERN = /^[a-f0-9-]{16,64}$/;

function createPlanningPerformanceChainId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const randomPart = () => Math.floor(Math.random() * 0x100000000)
    .toString(16)
    .padStart(8, "0");
  return `${randomPart()}-${randomPart()}-${randomPart()}`;
}

export function ensurePlanningPerformanceChainId(meta = {}) {
  const currentValue = typeof meta?.performanceChainId === "string"
    ? meta.performanceChainId.trim().toLowerCase()
    : "";
  return PERFORMANCE_CHAIN_ID_PATTERN.test(currentValue)
    ? currentValue
    : createPlanningPerformanceChainId();
}

function initBroadcastChannel() {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }

  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(PLANNING_STATE_CHANGE_CHANNEL);
    broadcastChannel.onmessage = (event) => {
      if (!event?.data || typeof event.data !== "object") {
        return;
      }

      emitPlanningStateChange(event.data, { fromBroadcast: true });
    };
  }

  return broadcastChannel;
}

function readSessionStorageNumber(key) {
  if (typeof sessionStorage === "undefined") {
    return 0;
  }

  try {
    const parsed = Number(sessionStorage.getItem(key));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeSessionStorageNumber(key, value) {
  if (typeof sessionStorage === "undefined" || !Number.isFinite(value) || value <= 0) {
    return;
  }

  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
}

function bumpPortailMutationToken() {
  const nextToken = readSessionStorageNumber(PORTAIL_MUTATION_SESSION_KEY) + 1;
  writeSessionStorageNumber(PORTAIL_MUTATION_SESSION_KEY, nextToken);
  return nextToken;
}

function syncPortailMutationTokenFromDetail(detail) {
  const receivedToken = Number(detail?.portailSyncToken);
  if (!Number.isFinite(receivedToken) || receivedToken <= 0) {
    return;
  }

  const currentToken = readSessionStorageNumber(PORTAIL_MUTATION_SESSION_KEY);
  if (receivedToken > currentToken) {
    writeSessionStorageNumber(PORTAIL_MUTATION_SESSION_KEY, receivedToken);
  }
}

export function readPortailPlanningMutationToken() {
  return readSessionStorageNumber(PORTAIL_MUTATION_SESSION_KEY);
}

export function readDataManagerSyncedPortailMutationToken() {
  return readSessionStorageNumber(DATA_MANAGER_PORTAIL_SYNC_SESSION_KEY);
}

export function markDataManagerPortailMutationSynced(token) {
  writeSessionStorageNumber(DATA_MANAGER_PORTAIL_SYNC_SESSION_KEY, token);
}

export function hasPendingDataManagerPortailMutationSync() {
  const latestToken = readPortailPlanningMutationToken();
  if (latestToken <= 0) {
    return false;
  }

  return latestToken > readDataManagerSyncedPortailMutationToken();
}

export function shouldRevalidateDataManagerOrdersFromPlanningChange(detail) {
  const source = typeof detail?.source === "string" ? detail.source.trim().toLowerCase() : "";
  return source === "dispatch" || source === "portail";
}

function normalizeDispatchPeriod(value) {
  return typeof value === "string" && value.trim().toLowerCase() === "evening"
    ? "evening"
    : "day";
}

export function readPlanningStateChangeScopeFromLocation() {
  if (typeof window === "undefined") {
    return {
      dispatchDate: "",
      dispatchPeriod: "day",
    };
  }
  const searchParams = new URLSearchParams(window.location.search || "");
  return {
    dispatchDate: String(searchParams.get("dispatch_date") || "").trim(),
    dispatchPeriod: normalizeDispatchPeriod(searchParams.get("dispatch_period")),
  };
}

export function isPlanningStateChangeInScope(
  detail,
  {
    dispatchDate = "",
    dispatchPeriod = "day",
  } = {},
) {
  const currentDispatchDate = String(dispatchDate || "").trim();
  const eventDispatchDate = String(
    detail?.dispatchDate || detail?.dispatch_date || "",
  ).trim();
  const affectedDispatchDates = Array.isArray(
    detail?.affectedDispatchDates || detail?.affected_dispatch_dates,
  )
    ? (detail.affectedDispatchDates || detail.affected_dispatch_dates)
      .map((value) => String(value || "").trim())
      .filter(Boolean)
    : [];
  if (
    currentDispatchDate
    && affectedDispatchDates.length > 0
    && !affectedDispatchDates.includes(currentDispatchDate)
  ) {
    return false;
  }
  if (
    currentDispatchDate
    && eventDispatchDate
    && currentDispatchDate !== eventDispatchDate
  ) {
    return false;
  }

  const eventDispatchPeriod = String(
    detail?.dispatchPeriod || detail?.dispatch_period || "",
  ).trim();
  return !eventDispatchPeriod
    || normalizeDispatchPeriod(eventDispatchPeriod) === normalizeDispatchPeriod(dispatchPeriod);
}

function emitPlanningStateChange(detail, { fromBroadcast = false } = {}) {
  planningStateChangeToken += 1;
  const payload = {
    ...detail,
    at: Date.now(),
    fromBroadcast,
    token: planningStateChangeToken,
  };

  if (detail?.source === "portail" && fromBroadcast) {
    syncPortailMutationTokenFromDetail(detail);
  }

  planningStateChangeListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {
      // Ignore listener errors to keep cross-view sync stable.
    }
  });
}

export function dispatchPlanningStateChange(source, meta = {}) {
  const normalizedSource = typeof source === "string" ? source.trim().toLowerCase() : "";
  if (!normalizedSource) {
    return false;
  }

  const implicitScope = readPlanningStateChangeScopeFromLocation();
  const detail = {
    source: normalizedSource,
    ...(implicitScope.dispatchDate ? implicitScope : {}),
    ...(meta && typeof meta === "object" ? meta : {}),
    performanceChainId: ensurePlanningPerformanceChainId(meta),
  };

  if (normalizedSource === "portail") {
    detail.portailSyncToken = bumpPortailMutationToken();
  }

  emitPlanningStateChange(detail);
  const channel = initBroadcastChannel();
  if (channel) {
    channel.postMessage(detail);
  }

  return true;
}

export function subscribePlanningStateChange(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  initBroadcastChannel();
  planningStateChangeListeners.add(listener);
  return () => {
    planningStateChangeListeners.delete(listener);
  };
}
